import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import MailIcon from "@mui/icons-material/Mail";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkAsUnreadIcon from "@mui/icons-material/MarkAsUnread";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTextDomainContext, useUser } from "@/app/providers";
import {
  useDeleteNotification,
  useGetNotification,
  usePageTitle,
  useReadNotification,
  useUnreadNotification,
  useUserProfile,
} from "@/hooks";
import { ConfirmNotification } from "@/components";
import { formatTimeAgo } from "@/utils";

interface NotificationDetailsProps {
  userId?: string;
}

export const NotificationDetails: React.FC<NotificationDetailsProps> = ({
  userId: propUserId,
}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ userId?: string; notificationId?: string }>();
  const userId = propUserId || params.userId;
  const notificationId = params.notificationId;

  const initialNotification = (location.state as any)?.notification;

  const { notification: fetchedNotification, loading: isFetching } =
    useGetNotification(notificationId);

  const notification = fetchedNotification || initialNotification;

  const { userProfile } = useUserProfile({
    userId: userId || undefined,
    email: !userId ? notification?.email || user?.email : undefined,
  });

  const subject = notification?.Subject || gettext("No Subject");

  usePageTitle(
    notification
      ? `${subject} - ${gettext("Notification")}`
      : gettext("Notification Details"),
  );

  const { readNotification, reading } = useReadNotification();
  const { unreadNotification, unreading } = useUnreadNotification(() => {
    if (notification?.id) {
      lastMarkedIdRef.current = notification.id;
    }
  });
  const { deleteNotification, deleting } = useDeleteNotification(() => {
    navigate(-1);
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const lastMarkedIdRef = useRef<string | null>(null);

  // Auto-mark as read when opened
  useEffect(() => {
    if (
      notification?.id &&
      !notification.Read &&
      lastMarkedIdRef.current !== notification.id
    ) {
      lastMarkedIdRef.current = notification.id;
      readNotification({ notificationId: notification.id });
    }
  }, [notification?.id, notification?.Read, readNotification]);

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const handleConfirmResult = (obj: any) => {
    if (obj.result === "yes" && notification?.id) {
      deleteNotification({
        notificationId: notification.id,
      });
    }
    setShowConfirm(false);
  };

  const userFullName =
    userProfile?.name ||
    (!userId || user?.id === userId || user?.email === notification?.email
      ? user?.name
      : "") ||
    "";

  const userEmail =
    notification?.email ||
    userProfile?.email ||
    (!userId || user?.id === userId ? user?.email : "") ||
    "";

  return (
    <Box sx={{ width: "100%", maxWidth: 1000, mx: "auto", py: 2 }}>
      {/* Top Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none" }}
        >
          {gettext("Back to Notifications")}
        </Button>

        {notification && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {!notification.Read ? (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<MarkEmailReadIcon />}
                onClick={() => {
                  if (notification?.id) {
                    lastMarkedIdRef.current = notification.id;
                  }
                  readNotification({ notificationId: notification.id });
                }}
                disabled={reading}
                sx={{ textTransform: "none" }}
              >
                {gettext("Mark as Read")}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<MarkAsUnreadIcon />}
                onClick={() => {
                  if (notification?.id) {
                    lastMarkedIdRef.current = notification.id;
                  }
                  unreadNotification({ notificationId: notification.id });
                }}
                disabled={unreading}
                sx={{ textTransform: "none" }}
              >
                {gettext("Mark as Unread")}
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={deleting}
              sx={{ textTransform: "none" }}
            >
              {gettext("Delete")}
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Email Card */}
      {isFetching && !notification ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !notification ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            {gettext("Notification not found")}
          </Typography>
        </Paper>
      ) : (
        <Card
          elevation={1}
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            overflow: "hidden",
          }}
        >
          {/* Email Subject Header */}
          <Box
            sx={{
              p: { xs: 2.5, md: 3 },
              pb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "grey.900" : "grey.50",
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                wordBreak: "break-word",
                flex: 1,
              }}
            >
              {subject}
            </Typography>

            <Chip
              icon={
                notification.Read ? (
                  <DraftsOutlinedIcon fontSize="small" />
                ) : (
                  <MailIcon fontSize="small" />
                )
              }
              label={notification.Read ? gettext("Read") : gettext("Unread")}
              size="small"
              color={notification.Read ? "default" : "primary"}
              variant={notification.Read ? "outlined" : "filled"}
              sx={{ fontWeight: 500 }}
            />
          </Box>

          {/* Email Meta: Sender, Recipient, Date */}
          <CardContent sx={{ p: { xs: 2.5, md: 3 }, pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 44,
                    height: 44,
                    fontWeight: 600,
                  }}
                >
                  S
                </Avatar>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      alignItems: "baseline",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {gettext("From")}:
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="text.primary"
                      sx={{ fontWeight: 600 }}
                    >
                      {gettext("System")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      alignItems: "baseline",
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {gettext("To")}:
                    </Typography>
                    {userFullName && userFullName !== userEmail ? (
                      <>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontWeight: 500 }}
                        >
                          {userFullName}
                        </Typography>
                        {userEmail && (
                          <Typography variant="body2" color="text.secondary">
                            &lt;{userEmail}&gt;
                          </Typography>
                        )}
                      </>
                    ) : userEmail ? (
                      <Typography variant="body2" color="text.secondary">
                        &lt;{userEmail}&gt;
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {notification?.date && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap", pt: 0.5 }}
                >
                  {formatTimeAgo(notification.date)}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Email Message Content */}
            <Box
              sx={{
                py: 2,
                px: { xs: 1, sm: 2 },
                minHeight: 200,
                typography: "body1",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.8,
                color: "text.primary",
                fontFamily: "inherit",
              }}
            >
              {notification.Message || gettext("No message content")}
            </Box>
          </CardContent>
        </Card>
      )}

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={{ id: notification?.id }}
        getResult={handleConfirmResult}
        message={gettext("Do you want to delete this notification?")}
      />
    </Box>
  );
};
